import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useOrder } from "../context/OrderContext";
import ProductGallery from "../components/ProductGallery";
import { Check, Star, Crown, Sparkles, Loader2 } from "lucide-react";

const TIER_ORDER = ["durango", "century", "premier"];

const TIER_META = {
  durango: {
    badgeIcon: Sparkles,
    badgeColor: "#fef3c7|#92400e", // bg|fg
    accent: false,
    cta: "Select Durango",
    plain: "The friendly entry-level slider. Lightweight, looks clean, gets the job done. Perfect if you want a real custom fit without paying for upgrades you won't notice.",
  },
  century: {
    badgeIcon: Star,
    badgeColor: "#dcfce7|#166534",
    accent: true, // highlight as recommended
    cta: "Select Century",
    plain: "Durango's bigger, beefier sibling. Heavier frame, smoother glide, rollers built for kids, dogs, and breezy summers. The sweet spot most people land on.",
  },
  premier: {
    badgeIcon: Crown,
    badgeColor: "#e9d5ff|#6b21a8",
    accent: false,
    cta: "Select Premier",
    plain: "Pure luxury. Stainless rollers, heavyweight hat-style frame, mechanically-fastened corners. Slides like butter, looks gorgeous, outlasts everything else on the rail.",
  },
};

export default function ChooseDoorPage() {
  const navigate = useNavigate();
  const { update, reset } = useOrder();
  const [doors, setDoors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picking, setPicking] = useState(null);

  useEffect(() => {
    api.get("/products").then((r) => {
      const tiers = r.data
        .filter((p) => p.type === "sliding_door" && p.tier)
        .sort((a, b) => TIER_ORDER.indexOf(a.tier) - TIER_ORDER.indexOf(b.tier));
      setDoors(tiers);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const choose = (door) => {
    setPicking(door.tier);
    // Reset any existing flow state, then load this product and jump to Quantity step
    reset();
    update({
      product: door,
      quantity: 1,
      stepId: "quantity", // skip product step → go straight to Quantity
    });
    navigate("/order");
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#f7faf6" }}>
      <header className="px-5 py-4 border-b bg-white sticky top-0 z-20">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
            <img src="https://customer-assets.emergentagent.com/job_tech-dispatch-31/artifacts/a6ta9f48_SCREENZZ-LOGO-RGB_TextOnly.png"
              alt="ScreenzZ" className="h-8" />
          </Link>
          <span className="step-pill">Choose your door</span>
        </div>
      </header>

      <main className="flex-1 px-5 py-8">
        <div className="max-w-3xl mx-auto text-center mb-8">
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-[var(--brand-text)]"
            data-testid="choose-door-headline">
            Choose Your Sliding Screen Door
          </h1>
          <p className="muted-text mt-3 text-base sm:text-lg">
            All doors are custom-built to your measurements.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <Loader2 className="w-6 h-6 mx-auto animate-spin brand-text" />
          </div>
        ) : (
          <div className="max-w-3xl mx-auto grid gap-5 sm:grid-cols-3 items-start">
            {doors.map((d) => {
              const meta = TIER_META[d.tier] || {};
              const BadgeIcon = meta.badgeIcon || Star;
              const [bg, fg] = (meta.badgeColor || "#e5e7eb|#374151").split("|");
              return (
                <div
                  key={d.slug}
                  data-testid={`door-card-${d.tier}`}
                  className={`relative bg-white rounded-3xl p-6 border-2 transition-transform hover:-translate-y-1 min-w-0 ${
                    meta.accent
                      ? "border-[var(--brand-green)] shadow-xl"
                      : "border-[var(--brand-border)] shadow-sm"
                  }`}
                  style={meta.accent ? { boxShadow: "0 12px 32px rgba(31, 122, 61, 0.18)" } : undefined}
                >
                  {meta.accent && (
                    <div
                      data-testid={`door-card-${d.tier}-recommended`}
                      className="absolute -top-3 left-1/2 -translate-x-1/2 brand-bg text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full whitespace-nowrap"
                    >
                      Recommended
                    </div>
                  )}

                  <div
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider"
                    style={{ background: bg, color: fg }}
                    data-testid={`door-card-${d.tier}-badge`}
                  >
                    <BadgeIcon className="w-3 h-3" />
                    {d.tagline}
                  </div>

                  <h2 className="mt-4 text-2xl font-extrabold text-[var(--brand-text)]">{d.name.replace(" Sliding Screen Door", "")}</h2>

                  {(d.image_url || (d.gallery_images && d.gallery_images.length > 0)) && (
                    <div className="mt-4" data-testid={`door-card-${d.tier}-image`}>
                      <ProductGallery
                        mainImage={d.image_url}
                        images={d.gallery_images || []}
                        productName={d.name}
                      />
                    </div>
                  )}

                  <div className="mt-3">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-black text-[var(--brand-text)]"
                        data-testid={`door-card-${d.tier}-price`}>
                        ${d.base_price.toFixed(0)}
                      </span>
                      <span className="text-sm font-semibold muted-text">+ tax</span>
                    </div>
                  </div>

                  <p className="muted-text text-sm mt-4 leading-snug">{d.description}</p>

                  <ul className="mt-4 space-y-2">
                    {(d.features || []).map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 brand-text mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {meta.plain && (
                    <div
                      data-testid={`door-card-${d.tier}-plain`}
                      className="mt-3 rounded-xl bg-[var(--brand-green-light)]/40 border border-[var(--brand-green-light)] px-3 py-2.5"
                    >
                      <p className="text-[11px] font-bold uppercase tracking-wider brand-text mb-1">In plain English</p>
                      <p className="text-sm text-[var(--brand-text)] leading-relaxed">{meta.plain}</p>
                    </div>
                  )}

                  <button
                    type="button"
                    data-testid={`door-card-${d.tier}-cta`}
                    onClick={() => choose(d)}
                    disabled={picking != null}
                    className={meta.accent ? "cta-btn mt-6" : "cta-btn mt-6"}
                    style={!meta.accent ? { background: "#0f1f17" } : undefined}
                  >
                    {picking === d.tier ? (
                      <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</span>
                    ) : (meta.cta || `Select ${d.tier}`)}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {!loading && doors.length > 0 && (
          <p className="max-w-md mx-auto text-center text-sm muted-text mt-8" data-testid="reassurance">
            Not sure which one to choose? Most customers go with{" "}
            <strong className="brand-text">Century</strong>{" "}
            for the best balance of price and durability.
          </p>
        )}
      </main>

      <footer className="py-6 text-center text-xs muted-text">
        <p>© {new Date().getFullYear()} ScreenzZ</p>
        <div className="flex items-center justify-center gap-4 mt-1 flex-wrap">
          <Link to="/privacy-policy"
            className="text-[11px] muted-text hover:brand-text underline-offset-2 hover:underline">
            Privacy
          </Link>
          <Link to="/terms"
            className="text-[11px] muted-text hover:brand-text underline-offset-2 hover:underline">
            Terms
          </Link>
          <a href="tel:8774000889"
            className="text-[11px] muted-text hover:brand-text underline-offset-2 hover:underline">
            Contact
          </a>
          <Link
            to="/admin/login"
            data-testid="footer-admin-link"
            className="text-[11px] muted-text hover:brand-text underline-offset-2 hover:underline"
          >
            Admin
          </Link>
        </div>
      </footer>
    </div>
  );
}
